import { forwardRef, type ButtonHTMLAttributes } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "../../lib/utils"
import type { AgentDescriptor } from "./types"

interface AgentBadgeProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "popover" | "type"> {
  agent: AgentDescriptor
  showCaret?: boolean
}

/**
 * Small clickable pill that shows the active agent. Displayed in
 * the composer; clicking opens the agent-switch popover. Visual
 * state dims when the agent isn't `available` so preview agents
 * read as faded.
 *
 * `forwardRef` + arbitrary-prop spread is deliberate — base-ui's
 * `Popover.Trigger render={element}` clones the element and
 * injects a ref, click handler, and ARIA props onto it. Without
 * forwardRef the ref injection fails silently and base-ui wraps
 * the element in a generic container that steals click area from
 * neighbouring buttons (the symptom: send / paperclip become
 * unclickable on the right edge in the composer because the
 * wrapper extends past the badge's visual bounds).
 */
export const AgentBadge = forwardRef<HTMLButtonElement, AgentBadgeProps>(
  function AgentBadge(
    { agent, className, showCaret = true, ...rest },
    ref,
  ) {
    const isPreview = !agent.available
    return (
      <button
        ref={ref}
        type="button"
        data-slot="agent-badge"
        aria-haspopup="listbox"
        aria-label={`Agent: ${agent.label}${isPreview ? " (preview)" : ""}`}
        {...rest}
        className={cn(
          "inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[0.7rem] font-medium",
          "border-black/10 bg-background text-foreground hover:bg-foreground/5",
          "dark:border-white/10",
          "transition-colors",
          isPreview && "opacity-60",
          className,
        )}
      >
        <span
          className="size-1.5 rounded-full bg-primary"
          aria-hidden="true"
        />
        <span className="truncate">{agent.label}</span>
        {isPreview && (
          <span className="text-muted-foreground">preview</span>
        )}
        {showCaret && (
          <ChevronDown className="size-3 opacity-60" aria-hidden="true" />
        )}
      </button>
    )
  },
)
